import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Card, CardBody } from '../ui/Card';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import api from '../../services/api';

/**
 * ResetPassword — Forgot password flow.
 * Flow: Enter email → Receive code → Enter code + new password
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'verify'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) { setError('Email is required'); return; }
    setLoading(true);
    setError(null);
    try {
      await api.post('/v1/auth/forgot-password', { email });
      setStep('verify');
    } catch (err) {
      // Don't reveal if email exists or not
      setStep('verify');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code) { setError('Verification code is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError(null);
    try {
      await api.post('/v1/auth/reset-password', { email, code, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError('Invalid code or password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardBody className="text-center py-8">
            <div className="mb-4"><CheckCircleIcon className="h-10 w-10 mx-auto" style={{ color: 'var(--green-600)' }} /></div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Password Reset!</h2>
            <p className="text-gray-600">Your password has been updated. Redirecting to login...</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Reset Password</h2>
          <p className="text-sm text-gray-500 mb-6">
            {step === 'request'
              ? 'Enter your email to receive a password reset code.'
              : 'Enter the code sent to your email and set a new password.'}
          </p>

          {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

          {step === 'request' && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Button type="submit" loading={loading} className="w-full">Send Reset Code</Button>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <Input label="Email" type="email" value={email} disabled />
              <Input label="Verification Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code from email" required />
              <Input label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Min 8 characters" />
              <Input label="Confirm Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              <Button type="submit" loading={loading} className="w-full">Reset Password</Button>
            </form>
          )}

          <p className="text-xs text-gray-400 text-center mt-4">
            Remember your password? <a href="/login" className="text-primary-600 hover:text-primary-800">Sign in</a>
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
